import { useState, useRef, useEffect } from "react";
import propTypes from "prop-types";
import useSupercluster from "use-supercluster";
import VytalList from "./VytalList";
import VytalMap from "./VytalMap";
import { useTranslation } from "react-i18next";
import {
  VytalPartnerSearchContainer,
  VytalPartnersContainer,
  VytalMapContainer,
  //VytalListContainer,
  NoStores,
  SearchInput,
  SearchInputContainer,
  SearchIconContainer,
  SearchSuggestionsContainer,
  SearchSuggestion,
} from "./VytalPartnersContainer";
import { ColorRing } from "react-loader-spinner";
import VytalLoader from "../assets/Lottie-Loader-fast-data.json"
import Lottie from "lottie-react";
import { gql, useQuery } from "@apollo/client";
import StoreCard from "./SubComponents";

const CITYZOOM = 10;
// Google always seems to return 5 suggestions
// https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest
const MAXSUGGESTIONS = 5;
const STOREZOOM = 14;

const getStoreQuery = gql`
query getStoreExtraInfoInMap($id: ID!) {
  store(id: $id) {
    ...criticalStoreDataForMap
    ...extraStoreDataForInlay
  }
}
fragment criticalStoreDataForMap on Store {
  id
  type
  brand {
    id
    name
  }
  employee_only
  name
  geo_hash
  coming_soon
  available_from
  available_soon
  opening_hours_multi {
    day
    time
  }
  lonlat {
    latitude
    longitude
  }
  address {
    streetname
    streetno
    street_extra
    zip
    city
    state
    country
  }
  website
  
}
fragment extraStoreDataForInlay on Store {
  id
  can_deliver
  can_preorder
  ordering_services {
    ordering_service_type
    url
  }
  product_tags
  images {
    url
  }
  accepted_container_types {
    id
    image_url
    name
  }
}
`;

// Width of the StoreCard panel on desktop/tablet
const SIDEPANEL_WIDTH = 500; // Corresponds to the 500px width in SubComponents.js

const VytalPartners = ({
  
  language,
  defaultCenter,
  defaultZoom,
  stores,
  showSearch, onBoundsChanged, currentBounds, currentZoom, loading
}) => {
  const { t } = useTranslation();
  const mapRef = useRef();
  const [googleMaps, setGoogleMaps] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [input, setInput] = useState("");
  const [selectedStore, setSelectedStore] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);

  const previousStoreData = useRef(null);

  const { data: storeData, loading: storeLoading } = useQuery(getStoreQuery, {
    variables: { id: selectedStore?.id },
    skip: !selectedStore?.id,
  });

  if (storeData?.store) {
    previousStoreData.current = storeData.store;
  }
  const displayedStore = storeData?.store || previousStoreData.current;

  useEffect(() => {
    if (googleMaps) {
      const places = googleMaps.places;
      const acS = new places.AutocompleteService();
      const pS = new places.PlacesService(mapRef.current);
      setAutocompleteService(acS);
      setPlacesService(pS);
    }
  }, [googleMaps]);

  useEffect(() => {
    if (showSearch) {
      const map = mapRef.current;
      if (input && predictions) {
        predictions.forEach((prediction) => {
          const { description, place_id } = prediction;
          if (description === input) {
            placesService.getDetails(
              { placeId: place_id, fields: ["geometry.location"] },
              (details) => {
                const newCenter = {
                  lat: details.geometry.location.lat(),
                  lng: details.geometry.location.lng(),
                };
                // const currentZoom = map.zoom;
                map.setZoom(CITYZOOM);
                map.panTo(newCenter);
                setShowSuggestions(false);
              }
            );
          }
        });
      }
      if (input) {
        const autocompletionRequest = {
          input: input,
          types: ["(cities)"],
          bounds: map.getBounds(),
        };
        autocompleteService.getPlacePredictions(
          autocompletionRequest,
          (predictions) => {
            setPredictions(predictions);
          }
        );
      } else {
        setPredictions(null);
      }

      setSelectedSuggestion(0);
      const inputElement = document.getElementById("searchInput");
      inputElement.value =
        inputElement.value !== input ? input : inputElement.value;
    }
  }, [input]);


  // Cluster stores for map
  const points = stores.map((store) => ({
    type: "Feature",
    properties: { cluster: false, storeId: store.id, store: store },
    geometry: {
      type: "Point",
      coordinates: [store.lonlat.longitude, store.lonlat.latitude],
    },
  }));

  const currentBbx = currentBounds ? [currentBounds.nw.lng, currentBounds.se.lat, currentBounds.se.lng, currentBounds.nw.lat] : null

  const { supercluster } = useSupercluster({
    points,
    currentBounds: currentBbx,
    currentZoom,
    options: {
      radius: 90,
      maxZoom: 14,
    },
  });

  // Collect all stores in the bounding box for list
  let clusters = [];
  let bbStores = [];
  if (supercluster && currentBbx && currentZoom) {
    supercluster.load(points)
    clusters = supercluster.getClusters(currentBbx, currentZoom);
    clusters.forEach((c) => {
      const {
        properties: { cluster, cluster_id, store },
      } = c;
      if (!cluster) {
        bbStores.push(store);
      } else {
        const clusterChildren = supercluster.getLeaves(cluster_id, 100);
        clusterChildren.forEach((c) => {
          const {
            properties: { store },
          } = c;
          bbStores.push(store);
        });
      }
    });
  }

  const handleSuggestionClick = (e) => {
    const value = e.target.textContent;
    setInput(value);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    setShowSuggestions(true);
  };

  const iframeWidth = "100%";
  const iframeHeight = "500px";

  const handlePinClick = (cluster) => {
    const store = cluster.properties.store;
    setSelectedStore(store);

    if (mapRef.current) {
        const {
            geometry: {
                coordinates: [longitude, latitude],
            },
        } = cluster;

        const map = mapRef.current;
        const windowWidth = window.innerWidth;
        
        let newCenter = { lat: latitude, lng: longitude };

        // On desktop/tablet, offset the pan to center the pin in the visible map area
        if (windowWidth >= 520) {
            const mapWidth = map.getDiv().offsetWidth;
            const panelWidth = SIDEPANEL_WIDTH; // Use consistent width for all desktop/tablet
            
            const mapBounds = map.getBounds();
            if (!mapBounds) return; // Bounds might not be available right away

            const longitudeSpan = mapBounds.toSpan().lng();
            
            // This is half the panel's width in longitude degrees
            const longitudeOffset = (panelWidth / mapWidth) * longitudeSpan / 2;
            
            // Pan the map to the left of the pin, so the pin appears centered in the visible area
            newCenter = { lat: latitude, lng: longitude - longitudeOffset };
        }
        
        map.panTo(newCenter);
    }
  };

  return (
    <VytalPartnerSearchContainer
      onClick={() => {
        setShowSuggestions(false);
      }}
    >
      {showSearch && (
        <SearchInputContainer>
          <SearchIconContainer />
          <SearchInput
            autoComplete="off"
            list="autoCompleteServiceSuggestions"
            id="searchInput"
            placeholder={t("type_your_city")}
            onFocus={(e) => {
              e.target.select();
            }}
            onClick={(e) => {
              setShowSuggestions(true);
              e.target.select();
            }}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                const int =
                  selectedSuggestion === MAXSUGGESTIONS - 1
                    ? MAXSUGGESTIONS - 1
                    : selectedSuggestion + 1;
                setSelectedSuggestion(int);
              }
              if (e.key === "ArrowUp") {
                const int =
                  selectedSuggestion === 0 ? 0 : selectedSuggestion - 1;
                setSelectedSuggestion(int);
              }
              if (e.key === "Enter") {
                const { description } = predictions[selectedSuggestion];
                setInput(description);
              }
            }}
          />

          <SearchSuggestionsContainer
            id="autoCompleteServiceSuggestions"
            showSuggestions={showSuggestions}
          >
            {predictions &&
              predictions.map((prediction, key) => {
                const { description } = prediction;
                const selected = key === selectedSuggestion;

                return (
                  <SearchSuggestion
                    key={key}
                    onClick={handleSuggestionClick}
                    selected={selected}
                  >
                    {description}
                  </SearchSuggestion>
                );
              })}
          </SearchSuggestionsContainer>
        </SearchInputContainer>
      )}
      <VytalPartnersContainer>
        <VytalMapContainer>
          {loading && (
            <div style={{ width: '40px', height: '40px', position: 'absolute', top: 10, left: 10, zIndex: 1000 }}>
              <Lottie autoplay={true} loop={true} animationData={VytalLoader} />
            </div>
          )}
          <VytalMap
            iframeWidth={iframeWidth}
            iframeHeight={iframeHeight}
            language={language}
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            onGoogleApiLoaded={({ map, maps }) => {
              mapRef.current = map;
              setGoogleMaps(maps);
            }}
            onChange={(e) => {
              onBoundsChanged(e)
            }}
            clusters={clusters}
            onClusterClick={(cluster) => {
              const {
                id,
                geometry: {
                  coordinates: [longitude, latitude],
                },
              } = cluster;
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(id),
                20
              );
              mapRef.current.setZoom(expansionZoom);
              mapRef.current.panTo({
                lat: latitude,
                lng: longitude,
              });
            }}
            onPinClick={handlePinClick}
            selectedStore={selectedStore}
          />
          <StoreCard
        store={displayedStore}
        isLoading={storeLoading && !!displayedStore?.id}
        onClose={() => {
          setSelectedStore(null);
        }}
        isVisible={!!displayedStore?.id && selectedStore?.id === displayedStore?.id}
      />
        </VytalMapContainer>
        
      </VytalPartnersContainer>
      
    </VytalPartnerSearchContainer>
  );
};

VytalPartners.defaultProps = {
  language: "en",
  // Köln
  defaultCenter: { lat: 50.935173, lng: 6.953101 },
  defaultZoom: 12,
  stores: [],
  showSearch: false,
};
/*


                  <VytalListContainer>
          <VytalList
            language={language}
            stores={bbStores}
            selectedStore={selectedStore}
            handleStoreClick={setSelectedStore}
          />
          {bbStores.length === 0 && mapRef.current && !loading && (
            <NoStores>
              <p>{t("no_partners_in_area")} </p>
              <p>
                <a
                  href="https://vytal.org/vytal-fuer-konsumenten#konsuinfo"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("suggest_partner")}
                </a>{" "}
                🙏✌️
              </p>
            </NoStores>
          )}
        </VytalListContainer>
*/
VytalPartners.propTypes = {
  language: propTypes.string,
  defaultCenter: propTypes.object,
  defaultZoom: propTypes.number,
  stores: propTypes.array,
  showSearch: propTypes.bool,
  onBoundsChanged: propTypes.func
};

export default VytalPartners;
